import { Button, Caption, Message, resetButtonStyle } from '@kvdbil/components';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { loginFlowStep } from '~/App/shared/actions/loginFlowActions';
import { sessionUpdate } from '~/App/shared/actions/session';
import { isObjectViewDrawerOpen as isBiddingViewOpenSelector } from '~/App/shared/selectors/storeObject';
import {
  idToken as idTokenSelector,
  member as memberSelector,
  personSelector
} from '~/App/shared/selectors/sessionSelector';
import { useBuyNowOpen } from '~/helpers/buyNow';
import { getCountryFromValue } from '~/helpers/countries';
import { updateMemberSimple } from '~/helpers/orchestration/member';
import { formErrors } from '~/helpers/validation';
import { useTranslation } from '~/Locale';
import {
  CountryField,
  NationalIdentificationField,
  PhoneNumberField
} from '../Fields/shared';
import useLocalization from '../../hooks/useLocalization';

const Form = styled.form`
  display: grid;
  grid-row-gap: 0.75rem;
`;

const ToggleSsnButton = styled.button<{ useSsn: boolean }>`
  ${resetButtonStyle};
  ${({ useSsn, theme }) =>
    useSsn
      ? `
    color: ${theme.colors.grayscaleToned.light1};
    text-decoration: underline;
  `
      : `
    color: ${theme.colors.secondary.main};
  `}
  text-align: left;
`;

interface Props {
  handleClose?: CallableFunction;
}
const VerificationForm = ({ handleClose }: Props) => {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const member = useSelector(memberSelector);
  const person = useSelector(personSelector);
  const idToken = useSelector(idTokenSelector);
  const { isSwedishLocale } = useLocalization();

  const isBiddingViewOpen = useSelector(isBiddingViewOpenSelector);
  const isBuyNowOpen = useBuyNowOpen();

  const countryValue = member?.addresses?.[0]?.country ?? '';
  const isMemberSwedish = Boolean(countryValue) && countryValue === 'se';
  const defaultCountry = !isMemberSwedish
    ? getCountryFromValue(countryValue, t)
    : undefined;

  const [useSsn, setUseSsn] = useState(
    (isSwedishLocale && !countryValue) || isMemberSwedish
  );

  const { handleSubmit, control, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      nationalIdentificationNumber:
        person?.nationalIdentificationCountry === 'se' || isMemberSwedish
          ? (person?.nationalIdentificationNumber ?? '')
          : '',
      phoneNumber: person?.phoneNumber ?? '',
      country: defaultCountry
    }
  });

  const onSubmit = () => {
    setError(null);
    setLoading(true);

    const values = getValues();

    const memberData = {
      addresses: !useSsn ? [{ country: values.country?.value }] : undefined,
      person: {
        phoneNumber: values.phoneNumber,
        nationalIdentificationNumber: useSsn
          ? values.nationalIdentificationNumber
          : undefined
      }
    };

    updateMemberSimple({ data: memberData, idToken: idToken })
      .then(({ data }) => {
        setLoading(false);

        dispatch(
          sessionUpdate({
            auth: data.auth,
            member: data.member,
            t
          })
        );

        if (isBiddingViewOpen || isBuyNowOpen) {
          typeof handleClose === 'function' && handleClose();
        } else {
          dispatch(loginFlowStep({ navigateCallback: history.push }));
        }
      })
      .catch((error: Record<string, string>[]) => {
        const errorMessage = formErrors(
          { errors: error },
          'response.data.message',
          true
        );
        setError(errorMessage[0]);
        setLoading(false);
      });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noValidate>
      {useSsn ? (
        <NationalIdentificationField control={control} t={t} />
      ) : (
        <CountryField control={control} t={t} disableSweden={!useSsn} />
      )}

      <ToggleSsnButton
        type="button"
        useSsn={useSsn}
        onClick={() => setUseSsn(!useSsn)}
      >
        <Caption as="span">
          {useSsn
            ? t('I have no Swedish identification number')
            : t('Validate using Swedish identification number')}
        </Caption>
      </ToggleSsnButton>

      <PhoneNumberField control={control} t={t} />

      {error && (
        <Message fullWidth withIcon type="error">
          {error}
        </Message>
      )}

      <Button color="secondary" fullWidth isLoading={isLoading} type="submit">
        {t('Save')}
      </Button>
    </Form>
  );
};

export default VerificationForm;
